import { useEffect, useState } from 'react';

import { api, devicesApi, exchangesApi, locationsApi, paymentProvidersApi, staffApi } from '@/services/api';

export interface AdminBadges {
  stores: number;      // Locations missing pickup method
  staff: number;       // 0 if no staff, else null
  devices: number;     // 0 if no devices, else null
  payments: number;    // 0 if no provider, else null
  orders: number;      // Pending exchanges count
  receipts: number;    // 0 if no delivery method, else null
  settings: number;    // 1 if using channel 1, else null
}

interface BadgeState {
  badges: AdminBadges;
  isLoading: boolean;
}

const EMPTY_BADGES: AdminBadges = {
  stores: 0,
  staff: 0,
  devices: 0,
  payments: 0,
  orders: 0,
  receipts: 0,
  settings: 0,
};

/**
 * Hook to fetch and compute badge counts for admin panel tabs.
 * Badges indicate items that need attention.
 */
export function useAdminBadges(): BadgeState {
  const [badges, setBadges] = useState<AdminBadges>(EMPTY_BADGES);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    async function loadBadges() {
      try {
        // Fetch all data in parallel
        const [
          locationsResult,
          pickupMethodsResult,
          staffResult,
          devicesResult,
          providersResult,
          exchangesResult,
          settingsResult,
          channelsResult,
        ] = await Promise.all([
          locationsApi.list().catch(() => ({ locations: [] })),
          api.getPickupMethods().catch(() => ({ methods: [] })),
          staffApi.list().catch(() => ({ staff: [] })),
          devicesApi.list().catch(() => ({ devices: [] })),
          paymentProvidersApi.list().catch(() => ({ providers: [] })),
          exchangesApi.list({ status: 'pending', limit: 100 }).catch(() => ({ exchanges: [] })),
          api.getSettings().catch(() => ({ regions: [], receiptOptions: null })),
          api.getChannels().catch(() => ({ currentChannelId: 0 })),
        ]);

        // Calculate badges
        const enabledLocations = locationsResult.locations.filter((l: { enabled: boolean }) => l.enabled);
        const pickupMethodLocationIds = new Set(
          pickupMethodsResult.methods.map((m: { locationId: number }) => m.locationId)
        );
        const locationsWithoutPickup = enabledLocations.filter(
          (l: { id: number }) => !pickupMethodLocationIds.has(l.id)
        );

        const hasNoStaff = staffResult.staff.length === 0;
        const hasNoDevices = devicesResult.devices.length === 0;
        const hasNoPaymentProvider = providersResult.providers.filter(
          (p: { enabled: boolean }) => p.enabled
        ).length === 0;

        const pendingExchanges = exchangesResult.exchanges.length;

        const receiptOptions = settingsResult.receiptOptions;
        const noReceiptMethods = receiptOptions
          ? !receiptOptions.emailEnabled && !receiptOptions.smsEnabled && !receiptOptions.printEnabled
          : false;

        const usingDefaultChannel = channelsResult.currentChannelId === 1;

        setBadges({
          stores: locationsWithoutPickup.length,
          staff: hasNoStaff ? 1 : 0,
          devices: hasNoDevices ? 1 : 0,
          payments: hasNoPaymentProvider ? 1 : 0,
          orders: pendingExchanges,
          receipts: noReceiptMethods ? 1 : 0,
          settings: usingDefaultChannel ? 1 : 0,
        });
      } catch (err) {
        console.error('Failed to load admin badges:', err);
        // Keep empty badges on error
      } finally {
        setIsLoading(false);
      }
    }

    loadBadges();
  }, []);

  return { badges, isLoading };
}
